import { Button } from "@/lib/components/Button/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/lib/components/Dialog";
import { Input } from "@/lib/components/Input";
import Toast from "@/lib/components/Toast";
import {
  useCreateCustomCaptionMutation,
  useUpdateCustomCaptionMutation,
} from "@/lib/queries/caption-queries";
import { useEditorStore } from "@/lib/stores/editor-store/editor-store";
import { Label } from "@/lib/components/Label";
import { CaptionDto } from "@repo/api/caption";
import { CompositionCaptionDto } from "@repo/api/editor";
import { useState } from "react";
import { RadioGroup, RadioGroupItem } from "@/lib/components/RadioGroup";

type SavePresetDialogProps = {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  selectedPreset: CaptionDto;
  compositionCaption: CompositionCaptionDto | null;
  teamExternalId: string;
  isCustomPreset: boolean;
  customCaptions: CaptionDto[];
};

function SavePresetDialog({
  open,
  onOpenChange,
  selectedPreset,
  compositionCaption,
  teamExternalId,
  isCustomPreset,
  customCaptions,
}: SavePresetDialogProps) {
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [name, setName] = useState(selectedPreset.name);
  const [nameError, setNameError] = useState<string | null>(null);

  const updateVideo = useEditorStore((state) => state.service.updateVideo);

  const createCustomCaption = useCreateCustomCaptionMutation({
    teamExternalId,
    onSuccess: (newCaption) => {
      Toast.info("Preset saved successfully");

      updateVideo((video) => {
        const captions = video.compositionJson.captions;
        if (captions && "externalId" in captions) {
          captions.externalId = newCaption.externalId;
          captions.config = newCaption.config;
        }
      });

      onOpenChange(false);
    },
  });

  const updateCustomCaption = useUpdateCustomCaptionMutation({
    teamExternalId,
    onSuccess: () => {
      Toast.info("Preset updated successfully");
      onOpenChange(false);
    },
  });

  const isSaving =
    createCustomCaption.isPending || updateCustomCaption.isPending;

  const handleSave = () => {
    if (!compositionCaption || isSaving) return;

    if (!isUpdating) {
      const trimmedName = name.replace(/\s{2,}/g, " ").trim();

      if (!trimmedName) return;

      const nameAlreadyExists = customCaptions.some(
        (c) => c.name.toLowerCase() === trimmedName.toLowerCase(),
      );

      if (nameAlreadyExists) {
        setNameError("A preset with this name already exists.");
        return;
      }

      setNameError(null);
      setName(trimmedName);

      createCustomCaption.mutate({
        name: trimmedName,
        config: compositionCaption.config,
      });
    } else {
      setNameError(null);

      updateCustomCaption.mutate({
        captionExternalId: selectedPreset.externalId,
        data: {
          name,
          config: compositionCaption.config,
        },
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="w-full">
          <DialogTitle>Save Preset</DialogTitle>
          <DialogDescription>
            {isCustomPreset
              ? "Do you want to replace the existing preset or save as a new one?"
              : "Enter a name for your new preset."}
          </DialogDescription>
        </DialogHeader>
        <div className="flex w-full flex-col gap-4">
          {isCustomPreset && (
            <RadioGroup
              value={isUpdating ? "update" : "new"}
              onValueChange={(value) => {
                if (value === "update") {
                  setIsUpdating(true);
                  setName(selectedPreset.name);
                  setNameError(null);
                } else {
                  setIsUpdating(false);
                }
              }}
              className="flex flex-col gap-3"
            >
              <div className="flex items-center gap-3">
                <RadioGroupItem value="update" id="save-mode-update" />
                <Label
                  htmlFor="save-mode-update"
                  className="text-sm text-text-primary"
                >
                  Replace current preset, &ldquo;{selectedPreset.name}&rdquo;
                </Label>
              </div>
              <div className="flex items-center gap-3">
                <RadioGroupItem value="new" id="save-mode-new" />
                <Label
                  htmlFor="save-mode-new"
                  className="text-sm text-text-primary"
                >
                  Save as new preset
                </Label>
              </div>
            </RadioGroup>
          )}
          <div className="w-full">
            <label className="input-label block">Preset Name</label>
            <Input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value.replace(/\s{2,}/g, " ").slice(0, 20));
                setNameError(null);
              }}
              placeholder="Enter preset name"
              maxLength={20}
            />
            {nameError && (
              <p className="mt-1 text-xs text-red-500">{nameError}</p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button
            onClick={handleSave}
            disabled={isSaving || (!isUpdating && !name.trim())}
          >
            {isSaving ? "Saving..." : "Save Preset"}
          </Button>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={isSaving}>
              Cancel
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SavePresetDialog;
